import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useLanguage } from '../../../../../contexts/LanguageContext';
import { getEmployeeEmail } from '../../../auth/employeeAuthStorage';
import DocumentChatShell from '../shells/DocumentChatShell';
import AssistantBotRow from '../shells/AssistantBotRow';
import ContinueButtonBar from '../components/ContinueButtonBar';
import ChatSaveErrorBanner from '../components/ChatSaveErrorBanner';
import { useDocumentChatNavigation } from '../shells/useDocumentChatNavigation';
import DocumentChatLayout from '../layout/DocumentChatLayout';
import { useChatThreadScroll } from '../hooks/useChatThreadScroll';
import { interpolateI18n } from '../utils/interpolateI18n';
import { buildOfficialFormTitleJa, reiwaYearFromDate } from './reiwaYear';
import DependentDeductionSectionMenu from './DependentDeductionSectionMenu';
import {
  allSectionsComplete,
  sectionStatusesAfterDependentsComplete,
  sectionStatusesAfterDependentsIncomplete,
  sectionStatusesAfterEmployeeComplete,
  sectionStatusesAfterEmployeeIncomplete,
  sectionStatusesAfterSpouseComplete,
  sectionStatusesAfterSpouseIncomplete,
  sectionStatusesAfterSpecialComplete,
  sectionStatusesAfterSpecialIncomplete,
  sectionStatusesAfterOtherIncomeComplete,
  sectionStatusesAfterOtherIncomeIncomplete,
} from './dependentDeductionSections';
import PdfPreviewActionButton from '../components/PdfPreviewActionButton';
import { useStaggeredPreviewCta } from '../hooks/useStaggeredPreviewCta';
import { useDependentDeductionFlow } from './useDependentDeductionFlow';
import { fetchTaxDeclarationProgress, upsertTaxDeclarationProgress } from './taxDeclarationApi';
import {
  otherClaimsSectionFlagsFromProgress,
  shouldOtherClaimsSectionBeDone,
} from './otherClaimsSection';
import { retirementAllowanceCursor } from './retirementAllowance/retirementAllowanceSteps';
import EmployeeSectionChatThread from './employeeSection/EmployeeSectionChatThread';
import EmployeeSectionChatComposer from './employeeSection/EmployeeSectionChatComposer';
import { useEmployeeSectionFlow } from './employeeSection/useEmployeeSectionFlow';
import SpouseSectionChatThread from './spouseSection/SpouseSectionChatThread';
import SpouseSectionChatComposer from './spouseSection/SpouseSectionChatComposer';
import { useSpouseSectionFlow } from './spouseSection/useSpouseSectionFlow';
import DependentsSectionChatThread from './dependentsSection/DependentsSectionChatThread';
import DependentsSectionChatComposer from './dependentsSection/DependentsSectionChatComposer';
import { useDependentsSectionFlow } from './dependentsSection/useDependentsSectionFlow';
import SpecialStatusChatThread from './specialStatus/SpecialStatusChatThread';
import SpecialStatusChatComposer from './specialStatus/SpecialStatusChatComposer';
import { useSpecialStatusFlow } from './specialStatus/useSpecialStatusFlow';
import OtherIncomeEarnerChatThread from './otherIncomeEarner/OtherIncomeEarnerChatThread';
import { useOtherIncomeEarnerFlow } from './otherIncomeEarner/useOtherIncomeEarnerFlow';
import RetirementAllowanceChatThread from './retirementAllowance/RetirementAllowanceChatThread';
import OtherClaimsChatComposer from './otherClaims/OtherClaimsChatComposer';
import {
  useRetirementAllowanceFlow,
} from './retirementAllowance/useRetirementAllowanceFlow';
import '../layout/DocumentChatLayout.css';

const FIRST_ASSISTANT_DELAY_MS = 1000;
const FOLLOW_ASSISTANT_GAP_MS = 650;

export interface DependentDeductionChatFlowProps {
  documentTitle: string;
  onBack: () => void;
  /** Spouse Section 2 confirm-only: open Family Info to edit spouse tax fields. */
  onOpenFamilyInfo?: () => void;
  onOpenPdfPreview?: () => void;
}

const DependentDeductionChatFlow: React.FC<DependentDeductionChatFlowProps> = ({
  documentTitle,
  onBack,
  onOpenFamilyInfo,
  onOpenPdfPreview,
}) => {
  const { t, language } = useLanguage();
  const { exitToChecklist } = useDocumentChatNavigation(onBack);
  const flow = useDependentDeductionFlow(t);
  const [revealPhase, setRevealPhase] = useState(0);
  const threadEndRef = useRef<HTMLDivElement | null>(null);

  const reiwaYear = useMemo(() => reiwaYearFromDate(), []);
  const calendarTaxYear = useMemo(() => new Date().getFullYear(), []);
  const email = useMemo(() => getEmployeeEmail(), []);

  const handleEmployeeSectionDoneChange = useCallback(
    (done: boolean) => {
      flow.setSectionStatuses((prev) =>
        done ? sectionStatusesAfterEmployeeComplete(prev) : sectionStatusesAfterEmployeeIncomplete(prev)
      );
    },
    [flow]
  );

  const employeeFlow = useEmployeeSectionFlow(
    email,
    calendarTaxYear,
    t,
    handleEmployeeSectionDoneChange
  );

  const handleSpouseSectionDoneChange = useCallback(
    (done: boolean) => {
      flow.setSectionStatuses((prev) =>
        done ? sectionStatusesAfterSpouseComplete(prev) : sectionStatusesAfterSpouseIncomplete(prev)
      );
    },
    [flow]
  );

  const spouseFlow = useSpouseSectionFlow(
    email,
    calendarTaxYear,
    t,
    handleSpouseSectionDoneChange,
    onOpenFamilyInfo
  );

  const handleDependentsSectionDoneChange = useCallback(
    (done: boolean) => {
      flow.setSectionStatuses((prev) =>
        done
          ? sectionStatusesAfterDependentsComplete(prev)
          : sectionStatusesAfterDependentsIncomplete(prev)
      );
    },
    [flow]
  );

  const dependentsFlow = useDependentsSectionFlow(
    email,
    calendarTaxYear,
    t,
    handleDependentsSectionDoneChange
  );

  const handleSpecialStatusSectionDoneChange = useCallback(
    (done: boolean) => {
      flow.setSectionStatuses((prev) =>
        done ? sectionStatusesAfterSpecialComplete(prev) : sectionStatusesAfterSpecialIncomplete(prev)
      );
    },
    [flow]
  );

  const specialStatusFlow = useSpecialStatusFlow(
    email,
    calendarTaxYear,
    t,
    handleSpecialStatusSectionDoneChange,
    onOpenFamilyInfo
  );

  const handleOtherIncomeSectionDoneChange = useCallback(
    (done: boolean) => {
      flow.setSectionStatuses((prev) =>
        done ? sectionStatusesAfterOtherIncomeComplete(prev) : sectionStatusesAfterOtherIncomeIncomplete(prev)
      );
    },
    [flow]
  );

  const otherIncomeEarnerFlow = useOtherIncomeEarnerFlow(
    email,
    calendarTaxYear,
    t,
    handleOtherIncomeSectionDoneChange
  );

  const otherIncomeBlockComplete = otherIncomeEarnerFlow.isOtherIncomeBlockComplete;
  const inOtherIncomeSection = flow.view === 'inSection' && flow.activeSectionId === 'otherIncomeEarner';
  const retirementFlowActive =
    inOtherIncomeSection && otherIncomeBlockComplete && !otherIncomeEarnerFlow.isLoading;

  const retirementAllowanceFlow = useRetirementAllowanceFlow(
    email,
    calendarTaxYear,
    t,
    retirementFlowActive,
    handleOtherIncomeSectionDoneChange
  );

  const inRetirementPhase = retirementFlowActive && !retirementAllowanceFlow.isComplete;

  const officialTitleJa = useMemo(() => buildOfficialFormTitleJa(reiwaYear), [reiwaYear]);

  const formComplete = allSectionsComplete(flow.sectionStatuses);
  const previewIntro = useMemo(
    () =>
      interpolateI18n(t('submitDocuments.dependentDeduction.previewIntro'), {
        documentTitle,
      }),
    [t, documentTitle]
  );
  const { showIntro: showPreviewIntroBubble, showCta: showPreviewPdfCta } = useStaggeredPreviewCta(
    formComplete && flow.view === 'sectionMenu'
  );

  const introBubble1 = useMemo(
    () =>
      interpolateI18n(t('submitDocuments.dependentDeduction.introBubble1'), {
        documentTitle,
        reiwaYear: String(reiwaYear),
        officialTitleJa,
      }),
    [t, documentTitle, reiwaYear, officialTitleJa]
  );

  const introBubble2 = useMemo(
    () => t('submitDocuments.dependentDeduction.introBubble2'),
    [t]
  );

  useEffect(() => {
    let cancelled = false;
    const loadFlags = async () => {
      if (!email?.trim()) return;
      let data = await fetchTaxDeclarationProgress(email.trim(), calendarTaxYear);
      if (cancelled || !data) return;
      if (!data.sectionOtherIncomeDone && shouldOtherClaimsSectionBeDone(data)) {
        const synced = await upsertTaxDeclarationProgress({
          email: email.trim(),
          taxYear: calendarTaxYear,
          sectionOtherIncomeDone: true,
          currentStepId: retirementAllowanceCursor('review'),
        });
        if (synced) data = synced;
      }
      if (cancelled || !data) return;
      flow.applySectionFlags(otherClaimsSectionFlagsFromProgress(data));
    };
    void loadFlags();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps -- load once per email/year
  }, [email, calendarTaxYear]);

  useEffect(() => {
    setRevealPhase(0);
    flow.setView('intro');
    const t1 = window.setTimeout(() => setRevealPhase(1), FIRST_ASSISTANT_DELAY_MS);
    const t2 = window.setTimeout(() => setRevealPhase(2), FIRST_ASSISTANT_DELAY_MS + FOLLOW_ASSISTANT_GAP_MS);
    const t3 = window.setTimeout(() => {
      setRevealPhase(3);
      flow.setView('sectionMenu');
    }, FIRST_ASSISTANT_DELAY_MS + FOLLOW_ASSISTANT_GAP_MS * 2);
    return () => {
      window.clearTimeout(t1);
      window.clearTimeout(t2);
      window.clearTimeout(t3);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps -- reset intro when document changes
  }, [documentTitle]);

  const inEmployeeSection = flow.view === 'inSection' && flow.activeSectionId === 'employee';
  const inSpouseSection = flow.view === 'inSection' && flow.activeSectionId === 'spouse';
  const inDependentsSection = flow.view === 'inSection' && flow.activeSectionId === 'dependents';
  const inSpecialStatusSection = flow.view === 'inSection' && flow.activeSectionId === 'specialStatus';

  useEffect(() => {
    if (
      inEmployeeSection &&
      !employeeFlow.isLoading &&
      flow.sectionStatuses.employee === 'completed'
    ) {
      employeeFlow.enterReviewFromMenu();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- open completed section in review mode
  }, [inEmployeeSection, flow.activeSectionId, flow.sectionStatuses.employee, employeeFlow.isLoading]);

  useEffect(() => {
    if (
      inSpouseSection &&
      !spouseFlow.isLoading &&
      flow.sectionStatuses.spouse === 'completed'
    ) {
      spouseFlow.enterReviewFromMenu();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- open completed section in review mode
  }, [inSpouseSection, flow.activeSectionId, flow.sectionStatuses.spouse, spouseFlow.isLoading]);

  useEffect(() => {
    if (
      inDependentsSection &&
      !dependentsFlow.isLoading &&
      flow.sectionStatuses.dependents === 'completed'
    ) {
      dependentsFlow.enterReviewFromMenu();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- open completed section in review mode
  }, [inDependentsSection, flow.activeSectionId, flow.sectionStatuses.dependents, dependentsFlow.isLoading]);

  useEffect(() => {
      if (
        inSpecialStatusSection &&
        !specialStatusFlow.isLoading &&
        flow.sectionStatuses.specialStatus === 'completed' &&
        specialStatusFlow.reviewMode
      ) {
        specialStatusFlow.enterReviewFromMenu();
      }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- open completed section in review mode
  }, [inSpecialStatusSection, flow.activeSectionId, flow.sectionStatuses.specialStatus, specialStatusFlow.isLoading]);

  useEffect(() => {
    if (
      inOtherIncomeSection &&
      !otherIncomeEarnerFlow.isLoading &&
      flow.sectionStatuses.otherIncomeEarner === 'completed' &&
      otherIncomeEarnerFlow.reviewMode
    ) {
      otherIncomeEarnerFlow.enterReviewFromMenu();
      retirementAllowanceFlow.enterReviewFromMenu();
    }
  }, [
    inOtherIncomeSection,
    flow.activeSectionId,
    flow.sectionStatuses.otherIncomeEarner,
    otherIncomeEarnerFlow.isLoading,
  ]);

  const showSectionMenu = revealPhase >= 3 && flow.view !== 'intro';
  const showPreviewOnSectionMenu =
    formComplete && showPreviewPdfCta && Boolean(onOpenPdfPreview) && flow.view === 'sectionMenu';
  const showMenuFooter =
    revealPhase >= 3 &&
    !showPreviewOnSectionMenu &&
    (flow.view === 'sectionMenu' ||
      (flow.view === 'inSection' &&
        !inEmployeeSection &&
        !inSpouseSection &&
        !inDependentsSection &&
        !inSpecialStatusSection &&
        !inOtherIncomeSection));
  const showEmployeeCompleteFooter =
    inEmployeeSection &&
    !employeeFlow.isLoading &&
    !employeeFlow.editingFromReview &&
    (employeeFlow.reviewMode || employeeFlow.isComplete);
  const showSpouseCompleteFooter =
    inSpouseSection &&
    !spouseFlow.isLoading &&
    !spouseFlow.editingFromReview &&
    (spouseFlow.reviewMode || spouseFlow.isComplete);
  const showEmployeeComposer =
    inEmployeeSection &&
    !employeeFlow.isLoading &&
    (employeeFlow.editingFromReview || (!employeeFlow.reviewMode && !employeeFlow.isComplete));
  const showSpouseComposer =
    inSpouseSection &&
    !spouseFlow.isLoading &&
    (spouseFlow.editingFromReview || (!spouseFlow.reviewMode && !spouseFlow.isComplete)) &&
    (!spouseFlow.confirmOnlyFromFamilyInfo || spouseFlow.step === 'employeeEstimatedIncome');
  const showDependentsCompleteFooter =
    inDependentsSection &&
    !dependentsFlow.isLoading &&
    !dependentsFlow.editingFromReview &&
    (dependentsFlow.reviewMode || dependentsFlow.isComplete);
  const showDependentsComposer =
    inDependentsSection &&
    !dependentsFlow.isLoading &&
    !dependentsFlow.expandedReview &&
    (dependentsFlow.editingFromReview ||
      (!dependentsFlow.reviewMode && !dependentsFlow.isComplete));
  const showDependentsBackToReview =
    inDependentsSection && !dependentsFlow.isLoading && dependentsFlow.expandedReview;
  const showSpecialStatusCompleteFooter =
    inSpecialStatusSection &&
    !specialStatusFlow.isLoading &&
    (specialStatusFlow.reviewMode || specialStatusFlow.isComplete);
  const showSpecialStatusComposer =
    inSpecialStatusSection &&
    !specialStatusFlow.isLoading &&
    !specialStatusFlow.reviewMode &&
    !specialStatusFlow.isComplete;
  const showOtherClaimsComposer =
    inOtherIncomeSection &&
    !otherIncomeEarnerFlow.isLoading &&
    (!otherIncomeBlockComplete ||
      (inRetirementPhase &&
        !retirementAllowanceFlow.isLoading &&
        !retirementAllowanceFlow.reviewMode &&
        !retirementAllowanceFlow.isComplete));
  const otherClaimsComposerPhase = inRetirementPhase ? 'retirement' : 'otherIncome';
  const showRetirementCompleteFooter =
    inOtherIncomeSection &&
    otherIncomeBlockComplete &&
    !otherIncomeEarnerFlow.isLoading &&
    !retirementAllowanceFlow.isLoading &&
    (retirementAllowanceFlow.reviewMode || retirementAllowanceFlow.isComplete);

  const handleInSectionFooter = () => {
    if (inEmployeeSection && !employeeFlow.isComplete) return;
    if (inSpouseSection && !spouseFlow.isComplete) return;
    if (inDependentsSection && !dependentsFlow.isComplete) return;
    if (inSpecialStatusSection && !specialStatusFlow.isComplete) return;
    if (inOtherIncomeSection) {
      if (!retirementAllowanceFlow.isComplete) return;
      void retirementAllowanceFlow.ensureSectionPersisted().then((ok) => {
        if (ok) flow.handleFooterPrimary();
      });
      return;
    }
    flow.handleFooterPrimary();
  };

  useChatThreadScroll(threadEndRef, [
    revealPhase,
    flow.view,
    flow.activeSectionId,
    flow.completedCount,
    flow.showEditHint,
    employeeFlow.isLoading,
    employeeFlow.fullNameCommitted,
    employeeFlow.fullNameKanaCommitted,
    employeeFlow.dobCommitted,
    employeeFlow.myNumberCommitted,
    employeeFlow.addressCommitted,
    employeeFlow.headOfHouseholdCommitted,
    employeeFlow.headNameCommitted,
    employeeFlow.headRelationshipCommitted,
    employeeFlow.multipleEmployersCommitted,
    employeeFlow.isComplete,
    employeeFlow.reviewMode,
    employeeFlow.step,
    spouseFlow.isLoading,
    spouseFlow.isComplete,
    spouseFlow.reviewMode,
    spouseFlow.step,
    spouseFlow.sharedLivelihoodCommitted,
    spouseFlow.employeeIncomeCommitted,
    spouseFlow.familyWorkerCommitted,
    spouseFlow.spouseIncomeCommitted,
    spouseFlow.ineligibleCommitted,
    spouseFlow.confirmNameCommitted,
    spouseFlow.changeReasonCommitted,
    spouseFlow.changeReasonOtherCommitted,
    spouseFlow.changeReasonOtherDraft,
    dependentsFlow.isLoading,
    dependentsFlow.step,
    dependentsFlow.completed.length,
    dependentsFlow.editIndex,
    dependentsFlow.isComplete,
    dependentsFlow.hasDependentsCommitted,
    dependentsFlow.addAnotherCommitted,
    specialStatusFlow.isLoading,
    specialStatusFlow.isComplete,
    specialStatusFlow.reviewMode,
    specialStatusFlow.step,
    specialStatusFlow.committed.size,
    otherIncomeEarnerFlow.isLoading,
    otherIncomeEarnerFlow.isComplete,
    otherIncomeEarnerFlow.isOtherIncomeBlockComplete,
    otherIncomeEarnerFlow.reviewMode,
    otherIncomeEarnerFlow.step,
    inRetirementPhase,
    retirementAllowanceFlow.isLoading,
    retirementAllowanceFlow.isComplete,
    retirementAllowanceFlow.reviewMode,
    retirementAllowanceFlow.step,
    formComplete,
    showPreviewIntroBubble,
    showPreviewPdfCta,
  ]);

  return (
    <DocumentChatShell onBack={exitToChecklist} cancelLabel={t('submitDocuments.chat.cancelProcedure')}>
      <DocumentChatLayout
        thread={
          <>
            {revealPhase >= 1 && <AssistantBotRow animateIn>{introBubble1}</AssistantBotRow>}
            {revealPhase >= 2 && <AssistantBotRow animateIn>{introBubble2}</AssistantBotRow>}
            {showSectionMenu && flow.view === 'sectionMenu' && (
              <>
                <AssistantBotRow animateIn>
                  <DependentDeductionSectionMenu
                    t={t}
                    sectionLabelKey={flow.sectionLabelKey}
                    sectionStatuses={flow.sectionStatuses}
                    allowRowClick={flow.allowRowClick}
                    showEditHint={flow.showEditHint}
                    onSelectSection={flow.enterSection}
                  />
                </AssistantBotRow>
                {formComplete && showPreviewIntroBubble && (
                  <AssistantBotRow animateIn>{previewIntro}</AssistantBotRow>
                )}
              </>
            )}
            {flow.view === 'inSection' && flow.activeSectionId && (
              <>
                <AssistantBotRow animateIn>
                  <DependentDeductionSectionMenu
                    t={t}
                    sectionLabelKey={flow.sectionLabelKey}
                    sectionStatuses={flow.sectionStatuses}
                    allowRowClick={flow.allowRowClick}
                    showEditHint={false}
                    onSelectSection={flow.enterSection}
                  />
                </AssistantBotRow>
                {inEmployeeSection ? (
                  employeeFlow.isLoading ? (
                    <AssistantBotRow animateIn>
                      {t('submitDocuments.dependentDeduction.employee.loading')}
                    </AssistantBotRow>
                  ) : (
                    <EmployeeSectionChatThread flow={employeeFlow} />
                  )
                ) : inSpouseSection ? (
                  spouseFlow.isLoading ? (
                    <AssistantBotRow animateIn>
                      {t('submitDocuments.dependentDeduction.spouse.loading')}
                    </AssistantBotRow>
                  ) : (
                    <SpouseSectionChatThread flow={spouseFlow} />
                  )
                ) : inDependentsSection ? (
                  dependentsFlow.isLoading ? (
                    <AssistantBotRow animateIn>
                      {t('submitDocuments.dependentDeduction.dependents.loading')}
                    </AssistantBotRow>
                  ) : (
                    <DependentsSectionChatThread flow={dependentsFlow} />
                  )
                ) : inSpecialStatusSection ? (
                  specialStatusFlow.isLoading ? (
                    <AssistantBotRow animateIn>
                      {t('submitDocuments.dependentDeduction.specialStatus.loading')}
                    </AssistantBotRow>
                  ) : (
                    <SpecialStatusChatThread flow={specialStatusFlow} />
                  )
                ) : inOtherIncomeSection ? (
                  otherIncomeEarnerFlow.isLoading ? (
                    <AssistantBotRow animateIn>
                      {t('submitDocuments.dependentDeduction.otherIncomeEarner.loading')}
                    </AssistantBotRow>
                  ) : (
                    <>
                      <OtherIncomeEarnerChatThread flow={otherIncomeEarnerFlow} />
                      {otherIncomeBlockComplete ? (
                        <>
                          <AssistantBotRow animateIn>
                            {t('submitDocuments.dependentDeduction.otherIncomeEarner.retirementTransition')}
                          </AssistantBotRow>
                          {retirementAllowanceFlow.isLoading ? (
                            <AssistantBotRow animateIn>
                              {t('submitDocuments.dependentDeduction.retirementAllowance.loading')}
                            </AssistantBotRow>
                          ) : (
                            <RetirementAllowanceChatThread
                              flow={retirementAllowanceFlow}
                              embedInOtherClaims
                            />
                          )}
                        </>
                      ) : null}
                    </>
                  )
                ) : (
                  <AssistantBotRow animateIn>
                    {interpolateI18n(t('submitDocuments.dependentDeduction.sectionPlaceholder'), {
                      sectionTitle: flow.sectionIntroText,
                    })}
                  </AssistantBotRow>
                )}
              </>
            )}
            <div ref={threadEndRef} aria-hidden />
          </>
        }
        composer={
          <>
            <ChatSaveErrorBanner
              message={
                employeeFlow.saveError
                  ? t('submitDocuments.dependentDeduction.employee.saveFailed')
                  : spouseFlow.saveError
                    ? t('submitDocuments.dependentDeduction.spouse.saveFailed')
                    : dependentsFlow.saveError
                      ? t('submitDocuments.dependentDeduction.dependents.saveFailed')
                      : specialStatusFlow.saveError
                        ? t('submitDocuments.dependentDeduction.specialStatus.saveFailed')
                        : otherIncomeEarnerFlow.saveError
                          ? t('submitDocuments.dependentDeduction.otherIncomeEarner.saveFailed')
                          : retirementAllowanceFlow.saveError
                            ? t('submitDocuments.dependentDeduction.retirementAllowance.saveFailed')
                            : null
              }
            />
            {showEmployeeComposer && (
              <EmployeeSectionChatComposer flow={employeeFlow} wheelLocale={language} />
            )}
            {showSpouseComposer && (
              <SpouseSectionChatComposer flow={spouseFlow} wheelLocale={language} />
            )}
            {showDependentsComposer && (
              <DependentsSectionChatComposer flow={dependentsFlow} wheelLocale={language} />
            )}
            {showSpecialStatusComposer && (
              <SpecialStatusChatComposer flow={specialStatusFlow} wheelLocale={language} />
            )}
            {showOtherClaimsComposer && (
              <OtherClaimsChatComposer
                phase={otherClaimsComposerPhase}
                otherIncomeFlow={otherIncomeEarnerFlow}
                retirementFlow={retirementAllowanceFlow}
                wheelLocale={language}
              />
            )}
            {showEmployeeCompleteFooter && (
              <ContinueButtonBar
                onContinue={flow.handleFooterPrimary}
                label={t('submitDocuments.dependentDeduction.completeSection')}
              />
            )}
            {showSpouseCompleteFooter && (
              <ContinueButtonBar
                onContinue={flow.handleFooterPrimary}
                label={t('submitDocuments.dependentDeduction.completeSection')}
              />
            )}
            {showDependentsBackToReview && (
              <ContinueButtonBar
                onContinue={dependentsFlow.cancelEdit}
                label={t('submitDocuments.familyInfo.backToReview')}
              />
            )}
            {showDependentsCompleteFooter && (
              <ContinueButtonBar
                onContinue={flow.handleFooterPrimary}
                label={t('submitDocuments.dependentDeduction.completeSection')}
              />
            )}
            {showSpecialStatusCompleteFooter && (
              <ContinueButtonBar
                onContinue={flow.handleFooterPrimary}
                label={t('submitDocuments.dependentDeduction.completeSection')}
              />
            )}
            {showRetirementCompleteFooter && (
              <ContinueButtonBar
                onContinue={flow.handleFooterPrimary}
                label={t('submitDocuments.dependentDeduction.completeSection')}
              />
            )}
            {showPreviewOnSectionMenu && (
              <PdfPreviewActionButton
                label={t('submitDocuments.dependentDeduction.previewPdf')}
                onClick={onOpenPdfPreview}
              />
            )}
            {showMenuFooter && (
              <ContinueButtonBar onContinue={handleInSectionFooter} label={flow.footerLabel} />
            )}
          </>
        }
      />
    </DocumentChatShell>
  );
};

export default DependentDeductionChatFlow;
